import React, { useState } from 'react';
import { useContracts } from '../../hooks/useContracts';
import { useWallet } from '../../context/WalletContext';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import { fmtUSDT } from '../../utils/format';
import { POOL_TYPES } from '../../utils/constants';
import ButtonLoader from '../common/ButtonLoader';

/**
 * Unified royalty card for Elite (1) / Pro (2) / Global (3).
 * Reads claimable from useUserData (no extra contract call here).
 */
const POOLS = {
  [POOL_TYPES.ELITE]: {
    title: 'Elite Royalty',
    share: '3%',
    accent: 'yellow',
    poolKey: 'eliteEligible',
    claimableKey: 'eliteClaimable',
    requirements: 'Pkg 4 active · 3 directs in Pkg 4 · Pkg 1 matrix L1–L4 complete'
  },
  [POOL_TYPES.PRO]: {
    title: 'Pro Royalty',
    share: '3%',
    accent: 'green',
    poolKey: 'proEligible',
    claimableKey: 'proClaimable',
    requirements: 'Pkg 7 active · ≥1 direct on Pkg 3+'
  },
  [POOL_TYPES.GLOBAL]: {
    title: 'Global Royalty',
    share: '4%',
    accent: 'yellow',
    poolKey: 'globalEligible',
    claimableKey: 'globalClaimable',
    requirements: 'Pkg 7 active · 3 directs in Pkg 7 · Pkg 1 matrix L1–L5 complete · 3 elite-eligible directs'
  }
};

const RoyaltyCard = ({ poolType, user, onChanged }) => {
  const cfg = POOLS[poolType];
  const { account } = useWallet();
  const { plans } = useContracts();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const eligible = !!user?.pools?.[cfg.poolKey];
  const claimableStr = user?.royalties?.[cfg.claimableKey] || '0';
  const hasClaimable = claimableStr !== '0';

  const onClaim = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    setBusy(true);
    try {
      const tx = await plans.write.claimRoyalty(poolType);
      notify.info('Claiming', `${cfg.title} claim submitted…`);
      await tx.wait();
      notify.success('Claimed', `${cfg.title} claimed`);
      onChanged && onChanged();
    } catch (e) {
      notify.error('Claim failed', parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`card ${cfg.accent === 'yellow' ? 'card-yellow' : 'card-green'}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ marginBottom: 0 }}>{cfg.title}</div>
        <span className={`badge ${cfg.accent === 'yellow' ? 'badge-yellow' : 'badge-green'}`}>{cfg.share}</span>
      </div>
      <div className={`card-value ${cfg.accent}`} style={{ marginTop: 10 }}>{fmtUSDT(claimableStr)}</div>
      <div className="card-sub">USDT claimable (gross)</div>

      <div className="elig-row" style={{ marginTop: 14 }}>
        <span>Eligibility</span>
        {eligible
          ? <span className="badge badge-green">Eligible</span>
          : <span className="badge badge-muted">Not yet</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <button
          className={`btn ${cfg.accent === 'yellow' ? 'btn-primary' : 'btn-success'}`}
          onClick={onClaim}
          disabled={busy || !eligible || !hasClaimable || !account}
        >
          {busy ? <ButtonLoader label="Claim" /> : 'Claim'}
        </button>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
        Requires: {cfg.requirements}
      </div>
      {!eligible && (
        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 6, lineHeight: 1.5 }}>
          Eligibility is granted automatically as soon as you meet the requirements above — no manual action needed.
        </div>
      )}
    </div>
  );
};

export default RoyaltyCard;
